import React, { useRef } from "react";
import { toast } from "react-toastify";

export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
export const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"];
export const BRAND = "#d01c4c";
// Light tints of the UtilStop colour (#d01c4c), so the screen is not plain white.
export const PAGE_BG = "#f8fafc";      // page background (stays the normal light grey)
export const CARD_BG = "#FFF6F8";      // cards / lists
export const SOFT_BG = "#FFEBF0";      // tiles, hover, buttons
export const CARD_BORDER = "#F8CCD7";  // card borders
export const DIVIDER = "#FBDDE5";      // lines between rows

// Reads a picked file as a base64 data URL, after checking type and size.
// Resolves to null (and shows a message) when the file is not acceptable.
export const readImageFile = (file) =>
  new Promise((resolve) => {
    if (!file) return resolve(null);
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error(`"${file.name}" is not supported. Please choose a PNG, JPG or WEBP image`);
      return resolve(null);
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(`The icon is too big (${(file.size / 1024 / 1024).toFixed(1)} MB). Please choose an image of 2 MB or smaller`);
      return resolve(null);
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => {
      toast.error("Could not read that image");
      resolve(null);
    };
    reader.readAsDataURL(file);
  });

export const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 };
export const inputStyle = { width: "100%", padding: "9px 11px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, color: "#0f172a", outline: "none", background: "#fff" };
export const primaryBtn = { background: BRAND, border: "none", color: "#fff", borderRadius: 9, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" };
export const ghostBtn = { background: SOFT_BG, border: "1px solid #F2B3C3", color: "#374151", borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" };

// Square thumbnail, with a letter placeholder when there is no image yet.
export function Thumb({ src, name, size = 44 }) {
  return src ? (
    <img
      src={src}
      alt={name || "icon"}
      style={{ width: size, height: size, borderRadius: 10, objectFit: "cover", border: "1px solid #e5e7eb", background: "#fff", flexShrink: 0 }}
      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
    />
  ) : (
    <div style={{ width: size, height: size, borderRadius: 10, background: "#FAD3DD", color: BRAND, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: size / 2.6, flexShrink: 0 }}>
      {(name || "?").trim().charAt(0).toUpperCase()}
    </div>
  );
}

// Image field for the offer / coupon forms: preview + choose / remove.
// Icon upload, same look as the service icon on the Add Service page: "Upload an Icon:" + an
// Upload button; once there is an image it shows as a 60x60 preview with Remove (or Upload, to
// change it, when canRemove is false - e.g. an already saved icon).
export const ICON_DIMENSIONS = { width: 260, height: 260 };

export function ImagePicker({ value, onChange, label = "Upload an Icon:", canRemove = true }) {
  const ref = useRef(null);
  const pick = async (e) => {
    const data = await readImageFile(e.target.files?.[0]);
    e.target.value = "";
    if (data) onChange(data);
  };
  const uploadBtn = (
    <button type="button" className="btn primaryColorText btn-outline-secondary ms-3 mb-2 mt-2" onClick={() => ref.current?.click()}>
      Upload
    </button>
  );
  return (
    <div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} style={{ display: "none" }} />
      {value ? (
        <div className="d-flex align-items-center">
          <img src={value} alt="Icon" className="img-fluid me-3 uploaded-image" height={60} width={60} style={{ objectFit: "contain" }} />
          {canRemove ? (
            <button type="button" className="btn primaryColorText btn-outline-secondary ms-2" onClick={() => onChange(null)}>
              Remove
            </button>
          ) : (
            uploadBtn
          )}
        </div>
      ) : (
        <>
          <label className="content mb-2">{label}</label>
          {uploadBtn}
        </>
      )}
      <div className="img-info" style={{ fontSize: 12, color: "#94a3b8" }}>
        Max Size: {MAX_IMAGE_BYTES / 1024 / 1024} MB
        <br />
        Dimensions: {ICON_DIMENSIONS.width}w x {ICON_DIMENSIONS.height}h
      </div>
    </div>
  );
}

export function ActivePill({ active }) {
  return (
    <span style={{ background: active ? "#DCFCE7" : "#F3F4F6", color: active ? "#15803D" : "#6B7280", borderRadius: 999, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>
      {active ? "Active" : "Off"}
    </span>
  );
}

export const fmtDate = (ymd) => {
  if (!ymd) return "—";
  const d = new Date(`${ymd}T00:00:00`);
  return isNaN(d) ? ymd : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};
